import { useApolloClient } from '@apollo/client';
import React, { PropsWithChildren, useCallback, useEffect, useRef } from 'react';
import { useFeatureFlag } from 'src/feature-flags/consumers';
import { IntercomUserJwtDocument, IntercomUserJwtQuery } from 'src/gqlReactTypings.generated.d';
import { useCurrentUser } from 'src/shared/hooks/useCurrentUserHook';
import { useIsTableRunHost } from 'src/shared/hooks/useIsTableRunHost';

const INTERCOM_APP_ID = process.env.REACT_APP_INTERCOM_APP_ID;
const INTERCOM_API_BASE = 'https://api-iam.intercom.io';
const REFRESH_MS = 4 * 60 * 1000; // refresh ~every 4 minutes for short-lived tokens

function loadIntercomScript(appId: string): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();

  // If already loaded
  if (window.Intercom && (window as any).intercomSettings?.app_id === appId) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    (function () {
      const w = window as any;
      const ic = w.Intercom;
      if (typeof ic === 'function') {
        resolve();
        return;
      }
      const i = function (...args: any[]) {
        // queue commands until script loads
        (i as any).c = (i as any).c || [];
        (i as any).c.push(args);
      } as any;
      (w as any).Intercom = i;
      const d = document;
      const s = d.createElement('script');
      s.type = 'text/javascript';
      s.async = true;
      s.src = `https://widget.intercom.io/widget/${appId}`;
      s.onload = () => resolve();
      const x = d.getElementsByTagName('script')[0];
      x.parentNode?.insertBefore(s, x);
    })();
  });
}

export const IntercomProvider: React.FC<PropsWithChildren<{ children: React.ReactNode }>> = ({ children }) => {
  const apollo = useApolloClient();
  const [currentUser] = useCurrentUser();
  const isTableRunHost = useIsTableRunHost();
  /**
   * TableRun support is not staffed yet, so the messenger stays down on that host and customers are
   * pointed at email instead. Booting it would also put a Shef-branded widget on tablerun.com.
   */
  const enabled = useFeatureFlag('intercomJwtEnabled') && !isTableRunHost;

  const bootedRef = useRef(false);
  const timerRef = useRef<number | null>(null);

  const getJwt = useCallback(async () => {
    const { data } = await apollo.query<IntercomUserJwtQuery>({
      query: IntercomUserJwtDocument,
      fetchPolicy: 'no-cache',
    });
    return data?.intercomUserJwt ?? '';
  }, [apollo]);

  const shutdown = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (bootedRef.current && window.Intercom) {
      window.Intercom('shutdown', {});
      bootedRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!enabled || !INTERCOM_APP_ID) {
      // If flag turns off, ensure shutdown
      shutdown();
      return;
    }

    if (!currentUser) {
      shutdown();
      return;
    }

    let cancelled = false;

    (async () => {
      await loadIntercomScript(INTERCOM_APP_ID);
      if (cancelled) return;

      const jwt = await getJwt();
      if (cancelled) return;

      const payload: any = {
        app_id: INTERCOM_APP_ID,
        api_base: INTERCOM_API_BASE,
        intercom_user_jwt: jwt,
      };

      if (!bootedRef.current) {
        window.Intercom?.('boot', payload as any);
        bootedRef.current = true;
      } else {
        window.Intercom?.('update', payload as any);
      }

      if (timerRef.current) {
        window.clearInterval(timerRef.current);
      }
      timerRef.current = window.setInterval(async () => {
        try {
          const newJwt = await getJwt();
          window.Intercom?.('update', { intercom_user_jwt: newJwt } as any);
        } catch (e) {
          console.error('Intercom JWT refresh failed', e);
        }
      }, REFRESH_MS);
    })().catch(console.error);

    return () => {
      cancelled = true;
    };
  }, [enabled, currentUser, getJwt, shutdown]);

  useEffect(() => {
    return () => shutdown();
  }, [shutdown]);

  return <>{children}</>;
};
